import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageCircle, MapPin, Clock, Send, HelpCircle } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/Toast';
interface ContactPageProps {
  onNavigate: (page: string) => void;
}
export function ContactPage({
  onNavigate
}: ContactPageProps) {
  const {
    success
  } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'support',
    message: ''
  });
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      success('Сообщение успешно отправлено! Мы ответим вам в ближайшее время.');
      setFormData({
        name: '',
        email: '',
        subject: 'support',
        message: ''
      });
    }, 1500);
  };
  return <div className="min-h-screen bg-slate-50 pb-24" data-id="element-18580">
      <div className="bg-white border-b border-slate-200 pt-6 pb-12" data-id="element-18581">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18582">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Контакты'
        }]} className="mb-8" data-id="element-18583" />
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" data-id="element-18584">
            Свяжитесь с нами
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl" data-id="element-18585">
            У вас есть вопросы, предложения или нужна помощь? Наша команда
            всегда готова помочь.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-18586">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-18587">
          {/* Contact Info */}
          <div className="lg:col-span-5 space-y-8" data-id="element-18588">
            <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm" data-id="element-18589">
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-18590">
                Контактная информация
              </h2>

              <div className="space-y-6" data-id="element-18591">
                <div className="flex items-start gap-4" data-id="element-18592">
                  <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0" data-id="element-18593">
                    <Mail className="w-5 h-5 text-indigo-600" data-id="element-18594" />
                  </div>
                  <div data-id="element-18595">
                    <div className="text-sm font-medium text-slate-500 mb-1" data-id="element-18596">
                      Email поддержки
                    </div>
                    <a href="mailto:support@astroguide.ru" className="text-slate-900 font-medium hover:text-indigo-600 transition-colors" data-id="element-18597">
                      support@astroguide.ru
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-18598">
                  <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center flex-shrink-0" data-id="element-18599">
                    <MessageCircle className="w-5 h-5 text-sky-600" data-id="element-18600" />
                  </div>
                  <div data-id="element-18601">
                    <div className="text-sm font-medium text-slate-500 mb-1" data-id="element-18602">
                      Telegram
                    </div>
                    <a href="#" className="text-slate-900 font-medium hover:text-sky-600 transition-colors" data-id="element-18603">
                      @astroguide_support
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-18604">
                  <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0" data-id="element-18605">
                    <Clock className="w-5 h-5 text-emerald-600" data-id="element-18606" />
                  </div>
                  <div data-id="element-18607">
                    <div className="text-sm font-medium text-slate-500 mb-1" data-id="element-18608">
                      Режим работы
                    </div>
                    <div className="text-slate-900 font-medium" data-id="element-18609">
                      Ежедневно, 10:00 - 20:00 (МСК)
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-indigo-50 rounded-2xl p-8 border border-indigo-100" data-id="element-18610">
              <div className="flex items-center gap-3 mb-4" data-id="element-18611">
                <HelpCircle className="w-6 h-6 text-indigo-600" data-id="element-18612" />
                <h2 className="text-lg font-bold text-indigo-900" data-id="element-18613">
                  Частые вопросы
                </h2>
              </div>
              <p className="text-indigo-800/80 mb-6 text-sm leading-relaxed" data-id="element-18614">
                Возможно, мы уже ответили на ваш вопрос. Посетите раздел FAQ,
                чтобы найти информацию о возвратах, отчетах и консультациях.
              </p>
              <Button variant="outline" className="w-full bg-white border-indigo-200 text-indigo-700 hover:bg-indigo-100" onClick={() => onNavigate('faq')} data-id="element-18615">
                Перейти в FAQ
              </Button>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7" data-id="element-18616">
            <div className="bg-white rounded-2xl p-8 sm:p-10 border border-slate-200 shadow-xl" data-id="element-18617">
              <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-18618">
                Написать нам
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-18619">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-18620">
                  <div className="space-y-2" data-id="element-18621">
                    <label className="text-sm font-medium text-slate-700" data-id="element-18622">
                      Ваше имя
                    </label>
                    <Input placeholder="Иван Иванов" required value={formData.name} onChange={(e: any) => handleChange('name', e.target ? e.target.value : e)} data-id="element-18623" />
                  </div>
                  <div className="space-y-2" data-id="element-18624">
                    <label className="text-sm font-medium text-slate-700" data-id="element-18625">
                      Email
                    </label>
                    <Input type="email" placeholder="ivan@example.com" required value={formData.email} onChange={(e: any) => handleChange('email', e.target ? e.target.value : e)} data-id="element-18626" />
                  </div>
                </div>

                <div className="space-y-2" data-id="element-18627">
                  <label className="text-sm font-medium text-slate-700" data-id="element-18628">
                    Тема обращения
                  </label>
                  <select className="w-full px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all" value={formData.subject} onChange={e => handleChange('subject', e.target.value)} data-id="element-18629">
                    <option value="support" data-id="element-18630">Техническая поддержка</option>
                    <option value="report" data-id="element-18631">Вопрос по отчету</option>
                    <option value="consultation" data-id="element-18632">Вопрос по консультации</option>
                    <option value="partnership" data-id="element-18633">Сотрудничество</option>
                    <option value="other" data-id="element-18634">Другое</option>
                  </select>
                </div>

                <div className="space-y-2" data-id="element-18635">
                  <label className="text-sm font-medium text-slate-700" data-id="element-18636">
                    Сообщение
                  </label>
                  <textarea className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all min-h-[150px] resize-y" placeholder="Опишите вашу проблему или вопрос максимально подробно..." required value={formData.message} onChange={e => handleChange('message', e.target.value)} data-id="element-18637"></textarea>
                </div>

                <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={isSubmitting} data-id="element-18638">
                  {isSubmitting ? 'Отправка...' : <>
                      <Send className="w-4 h-4 mr-2" data-id="element-18639" />
                      Отправить сообщение
                    </>}
                </Button>

                <p className="text-xs text-slate-500 mt-4" data-id="element-18640">
                  Нажимая кнопку «Отправить сообщение», вы соглашаетесь с{' '}
                  <button type="button" onClick={() => onNavigate('privacy')} className="text-indigo-600 hover:underline" data-id="element-18641">
                    Политикой конфиденциальности
                  </button>
                  .
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>;
}